// AssetCounter — integer stepper for the room-capture grid (PRD §7.3.1 /
// §7.4.1, buildspec 20-ui §5.1).
//
// +/− buttons flank a value region rendered as `role="spinbutton"`. Default
// button size is 56×56 px; compact size (for dense lists) is 48×48 px. Both
// remain at or above AGENTS.md invariant #10's 44 px minimum. The value is
// clamped to 0; by default a `−` tap at 0 is a no-op and the button is
// visually disabled. Callers that want a remove-on-extra-tap behaviour
// (room capture: tap `−` at 0 to drop the asset from this room
// instance) pass `onRemoveAtZero`; the button stays enabled at 0 and
// the tap is forwarded to that callback rather than being clamped.
//
// Press-and-hold on either button auto-repeats: after a 400 ms hold
// the counter ticks every 100 ms in the held direction, and each tick
// adds progressively more (1 → 2 → 5 → 10 — see HOLD_STEP_PROGRESSION)
// so a sustained hold can dial in counts of 50-200 without a 30 s
// finger-press. Quick taps (release before 400 ms) keep the existing
// single-tap onClick path with a fixed step of 1 — the trailing click
// after a fired hold is suppressed so a hold + release does not
// double-count. Decrement holds stop at 0 and never invoke
// `onRemoveAtZero`; the destructive remove can only fire from a
// discrete tap.
//
// Tap or long-press on the value opens a manual numeric-entry affordance via
// `onRequestManualEntry`. The `<NumericKeypadSheet>` primitive arrives in
// C2.10; this component exposes the callback seam so wiring it later is a
// one-line change in the parent row.
//
// Haptic feedback fires `navigator.vibrate(15)` on each recognised tap when
// available. This is **progressive enhancement** — iOS Safari does not
// implement the Vibration API — so the optimistic `onChange` is always
// called regardless of haptics, and the counter feels responsive without
// them.
//
// Live-region announcement: on every value change, "{assetName}: {value}"
// is emitted via the shared `<LiveRegion>` primitive for assistive tech.

import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Minus, Plus } from 'lucide-react'

import { LiveRegion } from '../../ui/live-region'
import { emit } from '../telemetry/counter-telemetry'
import {
  createGhostClickGuard,
  type CounterDirection,
} from './asset-counter-ghost-guard'

export interface AssetCounterProps {
  value: number
  onChange: (next: number) => void
  // Used for button aria-labels ("Increase {assetName} quantity") and the
  // live-region announcement ("{assetName}: {value}").
  assetName: string
  // Aria-label for the value spinbutton itself, e.g. "Light Fitting (LED)
  // quantity". Conventionally `${assetName} quantity`, but exposed
  // separately so callers can localise.
  ariaLabel: string
  size?: 'default' | 'compact'
  disabled?: boolean
  // Invoked on tap / long-press / keyboard-activate of the value region.
  // Wired to `<NumericKeypadSheet>` once C2.10 lands.
  onRequestManualEntry?: () => void
  // When provided, the `−` button stays enabled at value === 0 and a
  // tap fires this callback instead of the clamped no-op. Used by the
  // room-capture flow to "remove" an asset from a specific room when
  // the surveyor taps past zero.
  onRemoveAtZero?: () => void
}

const MIN = 0
const LONG_PRESS_MS = 500
// Press-and-hold on the +/- buttons. After HOLD_START_DELAY_MS the
// counter begins auto-repeating at HOLD_INTERVAL_MS per tick. Quick
// taps (release before the start delay) keep the existing single-tap
// onClick path. Decrement repeats stop at 0 — onRemoveAtZero is never
// invoked from a hold, only from a discrete tap, so the destructive
// remove cannot fire by accident at the end of a count-down.
export const HOLD_START_DELAY_MS = 400
export const HOLD_INTERVAL_MS = 100
// Trailing-click suppression lives in the per-direction ghost-click guard
// (asset-counter-ghost-guard.ts) — TIMING-INDEPENDENT and interleave-safe.
//
// Why not a window: a fired press-and-hold is followed by the browser's
// touch-compatibility `click` (the "ghost click"). Mobile historically delays
// it ~300 ms (the double-tap-zoom delay), and a busy main thread — a backed-up
// sync flushing "N changes not yet saved", the exact field condition — can
// stretch it arbitrarily. An earlier fix suppressed it inside a fixed window
// (250→700 ms); a click delayed past the window slipped through as a SECOND
// increment (the "asset quantities are double counting" report; reproduced on
// the live @powersync/web harness, a ≥400 ms press gave ±2). Its successor
// used a boolean cleared on the next pointerdown, which double-counted if a
// follow-up press landed before the delayed ghost (Codex P1). The guard uses a
// per-direction COUNTER instead — a fired hold's release enqueues one pending
// ghost, each real click consumes one, keyboard clicks never do — so it is
// correct regardless of delay, overlapping presses, or `touch-action` support.
// `touch-action: manipulation` (BUTTON_BASE / VALUE_BASE) is kept as a latency
// win that also makes the ghost prompt, but correctness no longer depends on it.

// Hold-repeat step acceleration: as the hold sustains, each tick adds
// progressively more so that users can dial in counts of 50-200 without
// holding for 30 seconds. Tick counts (not wall-clock time) drive the
// progression so behaviour is deterministic in tests. Past tick 30 the
// step stays at 10 forever — no upper cap on the value itself.
//
// Discrete taps always use step=1; this table is HOLD-ONLY.
export const HOLD_STEP_PROGRESSION: ReadonlyArray<{
  readonly untilTick: number
  readonly step: number
}> = [
  { untilTick: 5, step: 1 }, //  ticks 1-5   → +1
  { untilTick: 15, step: 2 }, //  ticks 6-15  → +2
  { untilTick: 30, step: 5 }, //  ticks 16-30 → +5
  { untilTick: Number.POSITIVE_INFINITY, step: 10 }, // ticks 31+ → +10
]

export function stepForTick(tick: number): number {
  for (const band of HOLD_STEP_PROGRESSION) {
    if (tick <= band.untilTick) return band.step
  }
  // Unreachable while the table ends with Infinity, but the explicit
  // fallback keeps the type signature total. If the table is ever
  // edited to remove the Infinity sentinel, fall back to step=1 — a
  // safe default that matches the discrete-tap behaviour.
  return 1
}

function triggerHaptic(): void {
  if (typeof navigator === 'undefined') return
  if (typeof navigator.vibrate !== 'function') return
  navigator.vibrate(15)
}

// `touch-manipulation` disables the legacy mobile double-tap-zoom
// gesture on these buttons, which removes the browser's ~300 ms
// synthetic-click delay after a touch release, so the trailing ghost
// click arrives promptly. Belt-and-braces with the timing-independent
// suppression in handleButtonClick.
const BUTTON_BASE =
  'inline-flex touch-manipulation items-center justify-center rounded-full border border-[color:var(--color-cc-forest)]/20 bg-white text-[color:var(--color-cc-forest)] shadow-sm transition-all hover:border-[color:var(--color-cc-forest)] hover:bg-[color:var(--color-cc-cream)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]'

// 48 px default (12 × 4 = 48). Matches PRD §7.3.2 minimum and keeps
// each tap target above the 44 px invariant.
const BUTTON_SIZE_DEFAULT = 'min-w-12 min-h-12 w-12 h-12'
// 44 px compact — the AGENTS.md / PRD floor for tap targets.
const BUTTON_SIZE_COMPACT = 'min-w-11 min-h-11 w-11 h-11'

// Value slot: tightened to read at the same scale as the asset
// name. Tabular nums keep digit widths uniform during increments.
// `touch-manipulation` — the value is a tap target too (tap / long-press
// opens manual entry), so it gets the same double-tap-zoom-delay removal
// as the +/- buttons; without it the keypad tap keeps the ~300 ms lag.
const VALUE_BASE =
  'inline-flex touch-manipulation min-w-9 min-h-11 items-center justify-center rounded-md px-1.5 font-display text-base font-semibold tabular-nums text-[color:var(--color-cc-forest)] select-none aria-disabled:cursor-not-allowed aria-disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]'

export function AssetCounter({
  value,
  onChange,
  assetName,
  ariaLabel,
  size = 'default',
  disabled = false,
  onRequestManualEntry,
  onRemoveAtZero,
}: AssetCounterProps) {
  const [announcement, setAnnouncement] = useState('')
  const previousValueRef = useRef(value)
  // Live mirror of `value` for hold-tick callbacks set up inside
  // setInterval — those closures cannot read fresh props directly, so
  // we read the latest value from this ref each tick.
  const valueRef = useRef(value)

  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    if (value !== previousValueRef.current) {
      previousValueRef.current = value
      setAnnouncement(`${assetName}: ${value}`)
    }
  }, [value, assetName])

  const canRemoveAtZero = onRemoveAtZero !== undefined
  const canDecrement = !disabled && (value > MIN || canRemoveAtZero)
  const canIncrement = !disabled

  const handleIncrement = useCallback(() => {
    if (!canIncrement) return
    triggerHaptic()
    emit({ name: 'counter.tap', direction: 'increment' })
    onChange(value + 1)
  }, [canIncrement, onChange, value])

  const handleDecrement = useCallback(() => {
    if (disabled) return
    if (value === MIN) {
      if (onRemoveAtZero === undefined) return
      triggerHaptic()
      emit({ name: 'counter.tap', direction: 'decrement' })
      onRemoveAtZero()
      return
    }
    triggerHaptic()
    emit({ name: 'counter.tap', direction: 'decrement' })
    onChange(Math.max(MIN, value - 1))
  }, [disabled, onChange, onRemoveAtZero, value])

  // Per-hold-session tick counter. Drives HOLD_STEP_PROGRESSION lookup.
  // Reset to 0 on every startHold; incremented before each tick fires
  // so the first tick uses stepForTick(1). Lives outside React state
  // because the hold loop fires inside setInterval.
  const holdTickCountRef = useRef(0)

  // Ref to the latest hold-tick callback so the setInterval closure
  // always reads fresh `disabled` / `onChange` / floor logic. Returns
  // true when a tick fired AND the loop should continue; false to
  // stop the loop (disabled, or decrement clamped at floor). The tick
  // optimistically advances `valueRef` before calling `onChange` so
  // consecutive ticks within a single event loop see the updated count
  // even before React commits the parent re-render. The step grows
  // with each tick per HOLD_STEP_PROGRESSION.
  const holdTickRef = useRef<(direction: CounterDirection) => boolean>(
    () => false,
  )
  useEffect(() => {
    holdTickRef.current = (direction) => {
      if (disabled) return false
      holdTickCountRef.current += 1
      const step = stepForTick(holdTickCountRef.current)
      if (direction === 'increment') {
        const next = valueRef.current + step
        valueRef.current = next
        emit({ name: 'counter.tap', direction: 'increment' })
        onChange(next)
        return true
      }
      // Decrement: stop at the floor. onRemoveAtZero is intentionally
      // NOT invoked from a hold — only a discrete tap is allowed to
      // trigger the destructive remove. When a stepped decrement would
      // overshoot 0 we clamp to 0 for this final tick AND stop the loop
      // so it doesn't keep firing zero-delta ticks.
      if (valueRef.current <= MIN) return false
      const next = Math.max(MIN, valueRef.current - step)
      const clamped = valueRef.current - step < MIN
      valueRef.current = next
      emit({ name: 'counter.tap', direction: 'decrement' })
      onChange(next)
      // If this tick landed at the floor by clamping, stop here so the
      // loop doesn't keep firing no-op ticks at 0.
      return !clamped
    }
  }, [disabled, onChange])

  const holdStartTimerRef = useRef<number | null>(null)
  const holdIntervalRef = useRef<number | null>(null)
  // Timing-independent, interleave-safe, per-direction ghost-click guard.
  // Lazy `useState` init gives one stable instance for the component's life.
  // See asset-counter-ghost-guard.ts for the state machine + its unit tests.
  const [ghost] = useState(createGhostClickGuard)

  const clearHoldTimers = useCallback(() => {
    if (holdStartTimerRef.current !== null) {
      window.clearTimeout(holdStartTimerRef.current)
      holdStartTimerRef.current = null
    }
    if (holdIntervalRef.current !== null) {
      window.clearInterval(holdIntervalRef.current)
      holdIntervalRef.current = null
    }
  }, [])

  const startHold = useCallback(
    (direction: CounterDirection) => {
      clearHoldTimers()
      // Each hold session starts fresh at tick 0 → first tick is step=1.
      holdTickCountRef.current = 0
      holdStartTimerRef.current = window.setTimeout(() => {
        // First repeat tick after the start delay.
        const fired = holdTickRef.current(direction)
        if (!fired) return
        // This press fired a hold → owe one ghost suppression for `direction`
        // NOW (not at release), so nothing between here and the click can drop
        // it (a pointerleave from drift, or a second press landing first).
        ghost.noteHoldFired(direction)
        holdIntervalRef.current = window.setInterval(() => {
          const ok = holdTickRef.current(direction)
          if (!ok) clearHoldTimers()
        }, HOLD_INTERVAL_MS)
      }, HOLD_START_DELAY_MS)
    },
    [clearHoldTimers, ghost],
  )

  // Pointerup: the finger lifted on the button, so its compat click is coming
  // to consume the owed ghost. Direction-agnostic (the ghost was already owed
  // for its direction at hold-fire), so a single shared handler serves both
  // buttons.
  const handlePointerUp = useCallback(() => {
    clearHoldTimers()
    ghost.noteRelease()
  }, [clearHoldTimers, ghost])
  // Pointercancel: the browser took the pointer (scroll/pan) — NO compat click
  // follows — so return the ghost this press owed.
  const handlePointerCancel = useCallback(() => {
    clearHoldTimers()
    ghost.noteCancel()
  }, [clearHoldTimers, ghost])
  // Pointerleave: NOT terminal — the finger can re-enter and release with a
  // click. Only stop the auto-repeat; do NOT drop the owed ghost (dropping it
  // here re-opened the drift-off-and-back double-count).
  const handlePointerLeave = useCallback(() => {
    clearHoldTimers()
  }, [clearHoldTimers])

  const handleIncrementPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return
      // Mark the start of a fresh press for the ghost guard on EVERY primary
      // pointerdown (even one that won't arm a hold), so a prior press's
      // hold-fired marker never leaks into this press.
      ghost.beginPress()
      if (!canIncrement) return
      startHold('increment')
    },
    [canIncrement, startHold, ghost],
  )

  const handleDecrementPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return
      if (disabled) return
      // Fresh press — must run even for the remove-at-zero tap below, which
      // returns before startHold(): otherwise a decrement hold that clamped at
      // 0 would leave the press marked hold-fired and the follow-up remove tap
      // would be mis-counted as that hold's ghost.
      ghost.beginPress()
      // Don't arm hold-repeat when a tap would fire onRemoveAtZero —
      // a held finger should never remove an asset from the room.
      if (valueRef.current <= MIN) return
      startHold('decrement')
    },
    [disabled, startHold, ghost],
  )

  const handleButtonClick = useCallback(
    (defaultHandler: () => void, direction: CounterDirection) =>
      (event: MouseEvent<HTMLButtonElement>) => {
        // The guard decides: keyboard clicks (detail === 0) always run; a
        // pointer/compat click (detail >= 1) runs unless it is the pending
        // ghost of a fired hold in THIS direction, which it consumes. See
        // asset-counter-ghost-guard.ts.
        if (ghost.shouldRunClick(direction, event.detail)) defaultHandler()
      },
    [ghost],
  )

  const openManualEntry = useCallback(() => {
    if (disabled) return
    if (onRequestManualEntry === undefined) return
    onRequestManualEntry()
  }, [disabled, onRequestManualEntry])

  const longPressTimerRef = useRef<number | null>(null)
  const longPressFiredRef = useRef(false)

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLSpanElement>) => {
      if (disabled) return
      // Only primary pointer button starts a long-press. Right-click /
      // middle-click / stylus eraser should not arm the timer.
      if (event.button !== 0) return
      longPressFiredRef.current = false
      longPressTimerRef.current = window.setTimeout(() => {
        longPressFiredRef.current = true
        openManualEntry()
      }, LONG_PRESS_MS)
    },
    [disabled, openManualEntry],
  )

  const handleValueClick = useCallback(() => {
    clearLongPressTimer()
    // If the long-press callback already fired, the keypad is already open;
    // suppress the tap that would open it a second time.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false
      return
    }
    openManualEntry()
  }, [clearLongPressTimer, openManualEntry])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (disabled) return
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        handleIncrement()
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        handleDecrement()
        return
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        openManualEntry()
      }
    },
    [disabled, handleDecrement, handleIncrement, openManualEntry],
  )

  useEffect(() => () => clearLongPressTimer(), [clearLongPressTimer])

  useEffect(() => () => clearHoldTimers(), [clearHoldTimers])

  const buttonSize = size === 'compact' ? BUTTON_SIZE_COMPACT : BUTTON_SIZE_DEFAULT

  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        className={`${BUTTON_BASE} ${buttonSize}`}
        onClick={handleButtonClick(handleDecrement, 'decrement')}
        onPointerDown={handleDecrementPointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerCancel}
        disabled={!canDecrement}
        aria-label={
          value === MIN && canRemoveAtZero
            ? `Remove ${assetName} from this room`
            : `Decrease ${assetName} quantity`
        }
      >
        <Minus aria-hidden="true" className="h-4 w-4" />
      </button>
      <span
        role="spinbutton"
        tabIndex={disabled ? -1 : 0}
        aria-valuemin={MIN}
        aria-valuenow={value}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        className={VALUE_BASE}
        onClick={handleValueClick}
        onPointerDown={handlePointerDown}
        onPointerUp={clearLongPressTimer}
        onPointerLeave={clearLongPressTimer}
        onPointerCancel={clearLongPressTimer}
        onKeyDown={handleKeyDown}
      >
        {value}
      </span>
      <button
        type="button"
        className={`${BUTTON_BASE} ${buttonSize}`}
        onClick={handleButtonClick(handleIncrement, 'increment')}
        onPointerDown={handleIncrementPointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerCancel}
        disabled={!canIncrement}
        aria-label={`Increase ${assetName} quantity`}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
      </button>
      <LiveRegion status={announcement} />
    </div>
  )
}
